// Pick a FOLDER on ACC (hubs > projects > folders) or Procore (companies > projects >
// Documents folders), or - mode "project" - just a Procore project. "Use this folder"
// returns { accountId, containerId, folderId, path } for the folder being shown.
//
// `start` opens the picker inside a project rather than at the top: the ACC project of
// the linked Revit file, or the Procore project the card is linked to.
import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { FolderOutlined, FileOutlined } from "@ant-design/icons";
import { getAccHubs, getAccProjects, getAccTopFolders, getAccFolder } from "components/Acc/accApi";
import { getProcoreCompanies, getProcoreProjects, getProcoreFolder } from "./integrationsApi";
import "components/Acc/acc.scss";

const ROOT_NAME = { acc: "Hubs", procore: "Companies" };

// `connection`: browse as that account (a destination's), not the person's primary
async function loadRows(provider, crumbs, connection) {
  const here = crumbs[crumbs.length - 1];
  const account = crumbs.find((c) => c.kind === "account");
  const container = crumbs.find((c) => c.kind === "container");
  if (here.kind === "root") {
    const rows = provider === "acc" ? await getAccHubs(connection) : await getProcoreCompanies(connection);
    return { rows: rows.map((r) => ({ kind: "account", id: r.id, name: r.name })) };
  }
  if (here.kind === "account") {
    const rows = provider === "acc" ? await getAccProjects(here.id, connection) : await getProcoreProjects(here.id, connection);
    return { rows: rows.map((r) => ({ kind: "container", id: r.id, name: r.name })) };
  }
  let entries;
  let selfId = here.kind === "folder" ? here.id : "";
  if (provider === "acc") {
    entries = here.kind === "container" ? await getAccTopFolders(account.id, here.id, connection) : await getAccFolder(container.id, here.id, connection);
  } else {
    const f = await getProcoreFolder(account.id, container.id, here.kind === "container" ? "root" : here.id, connection);
    entries = f.entries;
    // the project root is a real Documents folder with its own id
    selfId = f.id || selfId;
  }
  return { selfId, rows: entries
    .map((e) => ({ kind: e.type === "folder" ? "folder" : "file", id: e.id, name: e.name }))
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "folder" ? -1 : 1)) };
}

export default function FolderPicker({ provider, mode = "folder", start, onPick, onCancel, busy, connection = "" }) {
  const initial = [{ kind: "root", name: ROOT_NAME[provider] }];
  if (start && start.accountId && start.containerId) {
    initial.push({ kind: "account", id: start.accountId, name: start.accountName || "…" });
    initial.push({ kind: "container", id: start.containerId, name: start.containerName || "…" });
  }
  const [crumbs, setCrumbs] = useState(initial);
  const [rows, setRows] = useState(null);
  const [selfId, setSelfId] = useState("");
  const [error, setError] = useState("");
  const here = crumbs[crumbs.length - 1];
  const account = crumbs.find((c) => c.kind === "account");
  const container = crumbs.find((c) => c.kind === "container");

  useEffect(() => {
    let live = true;
    setRows(null);
    setSelfId("");
    setError("");
    loadRows(provider, crumbs, connection)
      .then((r) => {
        if (!live) return;
        setRows(r.rows);
        setSelfId(r.selfId || "");
      })
      .catch((e) => live && setError(e.message || "Could not read the folders"));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, crumbs.length, here.id, connection]);

  const path = crumbs.slice(1).map((c) => c.name).join(" / ");
  // a Procore project root is a real Documents folder; an ACC project has only top folders
  const canUseHere =
    mode === "project" ? here.kind === "container" : !!rows && !!selfId;

  const use = () =>
    onPick({
      accountId: account && account.id,
      accountName: account && account.name,
      containerId: container && container.id,
      containerName: container && container.name,
      folderId: mode === "project" ? "" : selfId,
      path,
    });

  return (
    <div className="acc-browser">
      <div className="acc-crumbs">
        {crumbs.map((c, i) => (
          <span key={`${c.kind}-${c.id || i}`}>
            {i > 0 ? " / " : ""}
            {i < crumbs.length - 1 ? <button onClick={() => setCrumbs(crumbs.slice(0, i + 1))}>{c.name}</button> : <b>{c.name}</b>}
          </span>
        ))}
      </div>
      {error ? <p className="text-danger">{error}</p> : null}
      {!rows && !error ? <Spin /> : null}
      {rows && !rows.length ? <p className="text-muted">Nothing here.</p> : null}
      {rows &&
        rows
          .filter((r) => !(mode === "project" && here.kind === "container"))
          .map((r) => (
            <button
              key={`${r.kind}-${r.id}`}
              className="acc-row"
              disabled={r.kind === "file"}
              onClick={() => setCrumbs([...crumbs, r])}
            >
              {r.kind === "file" ? <FileOutlined /> : <FolderOutlined />}
              <span>{r.name}</span>
            </button>
          ))}
      <div className="d-flex align-items-center mt-3" style={{ gap: 8 }}>
        <button className="header-btn text-white bg-bid border-bid px-3" disabled={!canUseHere || busy} onClick={use}>
          {mode === "project" ? "Link this project" : "Use this folder"}
        </button>
        {onCancel ? (
          <button className="bg-white text-bid border-bid shadow-none px-3" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        {!canUseHere ? (
          <small className="text-muted">{mode === "project" ? "Open a project to link it" : "Open the folder to deliver into"}</small>
        ) : (
          <small className="text-muted text-truncate">{path}</small>
        )}
      </div>
    </div>
  );
}
